/* ──────────────────────────────────────────────────────────────
   Dashboard/index.tsx
   ────────────────────────────────────────────────────────────── */

import React, { useState, useCallback } from 'react';
import { useReloadOnFocus } from '../navigation/useReloadOnFocus';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  Platform,
  ViewStyle,
  TextStyle,
} from 'react-native';
import { useTheme } from '../context/themeContext';
import { useResponsive } from '../lib/responsive';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { AppStackParamList } from '../navigation/types';
import { useSession } from '../context/sessionContext';
import { getItems } from '../services/catalog/item';
import { getItemTypes } from '../services/catalog/itemType';
import { getSuggestions } from '../services/catalog/suggestion';
import { getAttentionQueue } from '../services/catalog/bundle';
import {
  Icon,
  Button,
  Card,
  Chip,
  ConfBar,
  Thumb,
  QuotaBar,
} from '../components/ui';
import { useQuota } from '../context/quotaContext';
import { METRIC_CATALOG_ITEMS } from '../schemas/catalog/meter';

// ── Helpers ──────────────────────────────────────────────────

function fmtNum(n: number): string {
  return n.toLocaleString('en-IN');
}

// ── StatCard ─────────────────────────────────────────────────

interface StatCardProps {
  label: string;
  num: string | number;
  sub?: string;
  icon: string;
  tone: 'neutral' | 'success' | 'accent' | 'warn';
  onPress?: () => void;
  children?: React.ReactNode;
}

function StatCard({ label, num, sub, icon, tone, onPress, children }: StatCardProps) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii, shadows } = theme;
  const [hover, setHover] = useState(false);

  const toneFg: Record<string, string> = {
    neutral: colors.ink2,
    success: colors.success,
    accent:  colors.accent,
    warn:    colors.warn,
  };
  const fg = toneFg[tone];

  const cardStyle: ViewStyle = {
    backgroundColor: colors.card,
    borderWidth: 1,
    borderColor: hover ? fg + '66' : colors.line,
    borderRadius: radii.r3,
    padding: 18,
    overflow: 'hidden',
    ...(shadows.sm as ViewStyle),
    flex: 1,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
        transform: hover ? 'translateY(-2px)' : 'translateY(0)',
        boxShadow: hover ? `0 12px 28px ${fg}22` : undefined,
      } as ViewStyle,
      default: {},
    }),
  };

  return (
    <TouchableOpacity
      activeOpacity={0.85}
      onPress={onPress}
      // @ts-ignore web hover handlers
      onMouseEnter={Platform.OS === 'web' ? () => setHover(true) : undefined}
      onMouseLeave={Platform.OS === 'web' ? () => setHover(false) : undefined}
      style={cardStyle}
    >
      {/* faint corner glow */}
      <View
        // @ts-ignore web radial accent
        style={Platform.OS === 'web'
          ? ({ position: 'absolute', top: -34, right: -34, width: 110, height: 110, borderRadius: 55, background: `radial-gradient(circle, ${fg}1F, transparent 70%)` } as any)
          : { position: 'absolute', top: -34, right: -34, width: 110, height: 110, borderRadius: 55, backgroundColor: fg + '12' }}
      />
      {/* stat-top */}
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <Text style={{ fontSize: fontSize.sm, color: colors.ink3, fontWeight: fontWeight.medium, fontFamily: theme.font.sans }}>
          {label}
        </Text>
        {/* stat-ico — gradient chip */}
        <View
          // @ts-ignore web gradient chip
          style={{
            width: 36, height: 36, borderRadius: 11, alignItems: 'center', justifyContent: 'center',
            borderWidth: 1, borderColor: fg + '33',
            ...Platform.select({ web: { background: `linear-gradient(135deg, ${fg}30, ${fg}14)` } as any, default: { backgroundColor: fg + '1F' } }),
          }}
        >
          <Icon name={icon as any} size={17} color={fg} />
        </View>
      </View>
      {/* stat-num */}
      <Text style={{
        fontSize: fontSize['3xl'],
        fontWeight: fontWeight.semibold,
        letterSpacing: -0.02 * fontSize['3xl'],
        lineHeight: fontSize['3xl'],
        fontFamily: theme.font.mono,
        color: colors.ink,
        includeFontPadding: false,
      } as TextStyle}>
        {String(num)}
      </Text>
      {/* children (bar-track) */}
      {children}
      {/* stat-sub */}
      {sub ? (
        <Text style={{ fontSize: fontSize.sm, color: colors.ink3, marginTop: 8, fontFamily: theme.font.sans }}>
          {sub}
        </Text>
      ) : null}
    </TouchableOpacity>
  );
}

// ── BarTrack ──────────────────────────────────────────────────

interface BarSeg {
  k: string;
  c: string;
  width: number;
}

function BarTrack({ segs }: { segs: BarSeg[] }) {
  const { theme } = useTheme();
  const { colors, radii } = theme;

  return (
    <View style={{
      height: 9,
      borderRadius: radii.full,
      overflow: 'hidden',
      backgroundColor: colors.paper3,
      flexDirection: 'row',
      marginTop: 14,
    }}>
      {segs.map(s => (
        <View
          key={s.k}
          // @ts-ignore web gradient segment
          style={{
            width: `${s.width}%` as any,
            height: '100%' as any,
            backgroundColor: s.c,
            ...Platform.select({ web: { background: `linear-gradient(180deg, ${s.c}, ${s.c}CC)` } as any, default: {} }),
          }}
        />
      ))}
    </View>
  );
}

// ── ListRow ───────────────────────────────────────────────────

interface ListRowProps {
  onPress?: () => void;
  children: React.ReactNode;
  isLast?: boolean;
}

function ListRow({ onPress, children, isLast }: ListRowProps) {
  const { theme } = useTheme();
  const { colors } = theme;

  const inner = (
    <View style={{
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
      paddingVertical: 11,
      paddingHorizontal: 4,
      borderBottomWidth: isLast ? 0 : 1,
      borderBottomColor: colors.line,
    }}>
      {children}
    </View>
  );

  if (onPress) {
    return (
      <TouchableOpacity activeOpacity={0.8} onPress={onPress}>
        {inner}
      </TouchableOpacity>
    );
  }
  return inner;
}

// ── LrMain / LrTitle / LrSub ──────────────────────────────────

function LrMain({ children }: { children: React.ReactNode }) {
  return <View style={{ flex: 1, minWidth: 0 }}>{children}</View>;
}

function LrTitle({ children, mono }: { children: React.ReactNode; mono?: boolean }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight } = theme;
  return (
    <Text
      style={{
        fontWeight: fontWeight.medium,
        fontSize: fontSize.md,
        color: colors.ink,
        fontFamily: mono ? theme.font.mono : theme.font.sans,
      }}
      numberOfLines={1}
    >
      {children}
    </Text>
  );
}

function LrSub({ children, nowrap }: { children: React.ReactNode; nowrap?: boolean }) {
  const { theme } = useTheme();
  const { colors, fontSize } = theme;
  return (
    <Text
      style={{
        fontSize: fontSize.sm,
        color: colors.ink3,
        fontFamily: theme.font.sans,
      }}
      numberOfLines={nowrap ? 1 : 2}
    >
      {children}
    </Text>
  );
}

// ── ReviewBanner ─────────────────────────────────────────────

function ReviewBanner({ count, onPress }: { count: number; onPress: () => void }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii, shadows } = theme;

  return (
    <TouchableOpacity
      activeOpacity={0.85}
      onPress={onPress}
      style={[
        {
          flexDirection: 'row',
          alignItems: 'center',
          gap: 14,
          padding: 14,
          paddingHorizontal: 16,
          marginBottom: 16,
          backgroundColor: colors.warnSoft,
          borderWidth: 1,
          borderColor: colors.warnLine,
          borderRadius: radii.r3,
        },
        shadows.sm as ViewStyle,
      ]}
    >
      {/* icon */}
      <View style={{
        width: 34,
        height: 34,
        borderRadius: radii.r2,
        backgroundColor: colors.warn,
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}>
        <Icon name="alert-circle" size={18} color={colors.white} />
      </View>
      {/* text */}
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text style={{ fontWeight: fontWeight.semibold, fontSize: fontSize.md, color: colors.ink, fontFamily: theme.font.sans }}>
          {count} bundles need price review
        </Text>
        <Text style={{ fontSize: fontSize.sm, color: colors.ink3, fontFamily: theme.font.sans, marginTop: 2 }}>
          A component price changed via import or API — dynamic bundle rollups are held until you review them.
        </Text>
      </View>
      {/* button */}
      <Button
        kind="accent"
        size="sm"
        icon={<Icon name="arrow-r" size={12} color={colors.white} />}
        iconRight
        onPress={onPress}
      >
        Review now
      </Button>
    </TouchableOpacity>
  );
}

// ── Dashboard ─────────────────────────────────────────────────

export default function Dashboard() {
  const { theme } = useTheme();
  const navigation = useNavigation<NativeStackNavigationProp<AppStackParamList>>();
  const { user } = useSession();
  const { isBlocked, refresh: refreshQuota } = useQuota();
  const itemsBlocked = isBlocked(METRIC_CATALOG_ITEMS);
  const { colors, fontSize, fontWeight, radii, spacing, shadows } = theme;
  const firstName = user?.first_name?.trim() || 'there';
  // Time-of-day greeting (local time).
  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  })();

  // Live data (sk-catalog-api). Counts drive the KPI cards + review banner.
  const [items, setItems] = useState<any[]>([]);
  const [typesCount, setTypesCount] = useState(0);
  const [aiCount, setAiCount] = useState(0);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [attention, setAttention] = useState<any[]>([]);

  const load = useCallback(async () => {
    try {
      const [it, ty, sg, at] = await Promise.all([
        getItems({ limit: 200 }),
        getItemTypes(),
        getSuggestions({ status: 'PENDING' }).catch(() => ({ items: [] } as any)),
        getAttentionQueue({ limit: 50 }).catch(() => ({ items: [] } as any)),
      ]);
      // Exclude deleted (ARCHIVED) items from dashboard counts/activity.
      setItems((it.items ?? []).filter((x: any) => x.currentState !== 'ARCHIVED'));
      // The internal auto-created "Bundle" type isn't a user-facing item type
      // (bundles are managed on the Bundles screen and hidden from Item types
      // & fields), so exclude it from the KPI count — mirrors that screen.
      setTypesCount((ty.items ?? []).filter((t: any) => (t.name || '').toLowerCase() !== 'bundle').length);
      const sgItems = (sg as any).items ?? [];
      setAiCount(sgItems.length);
      setSuggestions(sgItems);
      setAttention((at as any).items ?? []);
    } catch { /* handled per-call */ }
  }, []);
  // Refetch whenever the dashboard regains focus (e.g. after creating a type).
  // Also refresh plan usage so the quota bar + gating reflect recent changes.
  useReloadOnFocus(useCallback(async () => { await Promise.all([load(), refreshQuota()]); }, [load, refreshQuota]));

  const byState = {
    PUBLISHED:   items.filter(i => i.currentState === 'PUBLISHED').length,
    DRAFT:       items.filter(i => i.currentState === 'DRAFT').length,
    SCHEDULED:   items.filter(i => i.currentState === 'SCHEDULED').length,
    DEACTIVATED: items.filter(i => i.currentState === 'DEACTIVATED').length,
  };
  const total = items.length;
  // Group the live pending suggestions by item so the panel lists which items
  // are awaiting review (joins to the item list for the SKU label).
  const aiItems = (() => {
    const byItem = new Map<string, any>();
    for (const s of suggestions) {
      const itemId = s.catalogItemId;
      if (!itemId) continue;
      if (!byItem.has(itemId)) {
        const it = items.find(i => i.id === itemId);
        byItem.set(itemId, { id: itemId, skuOrReference: it?.skuOrReference, emoji: '✨', ai: [] });
      }
      byItem.get(itemId).ai.push({ kind: s.kind, conf: Number(s.confidence) || 0 });
    }
    return Array.from(byItem.values());
  })();
  const mediaFails: any[] = [];
  const searchMisses: any[] = [];
  const priceReviews = attention;

  // Recent activity derived from live items (most recently updated first).
  const recentActivity = [...items]
    .sort((a, b) => new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime())
    .slice(0, 5)
    .map((it) => {
      const state = (it.currentState || 'DRAFT') as string;
      const tone = state === 'PUBLISHED' ? colors.success : state === 'DEACTIVATED' ? colors.danger : colors.ink3;
      const icon = state === 'PUBLISHED' ? 'check-circle' : state === 'DEACTIVATED' ? 'bolt-off' : 'box';
      return {
        id: it.id,
        act: state.charAt(0) + state.slice(1).toLowerCase(),
        what: it.skuOrReference || (it.id || '').slice(0, 8),
        when: it.updatedAt ? new Date(it.updatedAt).toLocaleDateString() : '',
        icon, tone,
      };
    });

  const barSegs = [
    { k: 'PUBLISHED',   c: colors.success,  width: total ? (byState.PUBLISHED   / total) * 100 : 0 },
    { k: 'DRAFT',       c: colors.neutral,  width: total ? (byState.DRAFT       / total) * 100 : 0 },
    { k: 'SCHEDULED',   c: colors.info,     width: total ? (byState.SCHEDULED   / total) * 100 : 0 },
    { k: 'DEACTIVATED', c: colors.danger,   width: total ? (byState.DEACTIVATED / total) * 100 : 0 },
  ].filter(s => s.width > 0);

  function failInfo(it: any) {
    const idx = (it.media || []).map((m: any, i: number) => m.status === 'FAILED' ? i + 1 : null).filter(Boolean) as number[];
    const live = it.state === 'PUBLISHED' || it.state === 'SCHEDULED';
    return { idx, live };
  }

  // Layout: single responsive source of truth (lib/responsive). Below the
  // mobile breakpoint we stack panels and shrink the header.
  const { isMobile, pad } = useResponsive();

  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: colors.paper }}
      contentContainerStyle={{ paddingTop: 24, paddingHorizontal: pad, paddingBottom: 64 }}
    >
      {/* ── Page header ────────────────────────────────────── */}
      <View style={{
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 16,
        marginBottom: 20,
      }}>
        {/* left */}
        <View style={{ flex: 1, minWidth: 220 }}>
          <Text
            numberOfLines={2}
            style={{
              fontSize: isMobile ? fontSize['2xl'] : fontSize['3xl'],
              fontWeight: fontWeight.semibold,
              letterSpacing: -0.02 * (isMobile ? fontSize['2xl'] : fontSize['3xl']),
              color: colors.ink,
              fontFamily: theme.font.sans,
              margin: 0,
            }}
          >
            {greeting}, {firstName}
          </Text>
          <Text style={{
            color: colors.ink3,
            fontSize: fontSize.md,
            marginTop: 4,
            fontFamily: theme.font.sans,
          }}>
            Here's the health of your catalog across all locations.
          </Text>
        </View>
        {/* right cluster */}
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <Button
            kind="default"
            size="sm"
            icon={<Icon name="import" size={13} color={colors.ink2} />}
            onPress={() => navigation.navigate('imports')}
          >
            Import
          </Button>
          <Button
            kind="accent"
            size="sm"
            icon={<Icon name="plus" size={13} color={colors.white} />}
            onPress={() => navigation.navigate('items', { compose: true })}
            disabled={itemsBlocked}
          >
            New item
          </Button>
        </View>
      </View>

      {/* ── Plan usage (metered) ──────────────────────────── */}
      <QuotaBar metric={METRIC_CATALOG_ITEMS} label="Catalog items" />

      {/* ── Stat grid ─────────────────────────────────────── */}
      <View style={{
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 14,
        marginBottom: 20,
      }}>
        {/* Total items */}
        <View style={{ flex: 1, minWidth: isMobile ? 220 : 160 }}>
          <StatCard
            label="Total items"
            num={fmtNum(total)}
            icon="box"
            tone="neutral"
            sub="Across all item types"
            onPress={() => navigation.navigate('items')}
          >
            <BarTrack segs={barSegs} />
          </StatCard>
        </View>

        {/* Item types */}
        <View style={{ flex: 1, minWidth: isMobile ? 220 : 160 }}>
          <StatCard
            label="Item types"
            num={fmtNum(typesCount)}
            icon="sliders"
            tone="accent"
            sub="Field sets defining your items"
            onPress={() => navigation.navigate('types')}
          />
        </View>

        {/* Published & live */}
        <View style={{ flex: 1, minWidth: isMobile ? 220 : 160 }}>
          <StatCard
            label="Published & live"
            num={byState.PUBLISHED}
            icon="check-circle"
            tone="success"
            sub={`${byState.SCHEDULED} scheduled · ${byState.DRAFT} in draft`}
            onPress={() => navigation.navigate('items', { state: 'PUBLISHED' })}
          />
        </View>

      </View>

      {/* ── Media card ────────────────────────────────────── */}
      {mediaFails.length > 0 && (
        <View style={{ marginTop: 16 }}>
          <Card
            title="Media needs a fix"
            sub="We publish with the good photos and strip failed ones — replace them so customers see the full set"
            padded={false}
            actions={
              <Chip tone="warn" icon={<Icon name="image" size={10} color={colors.warn} />}>
                {String(mediaFails.length)}
              </Chip>
            }
          >
            <View style={{ paddingHorizontal: 16, paddingBottom: 8 }}>
              {mediaFails.map((it, idx) => {
                const { idx: failIdx, live } = failInfo(it);
                return (
                  <ListRow
                    key={it.id}
                    onPress={() => navigation.navigate('editor', { id: it.id })}
                    isLast={idx === mediaFails.length - 1}
                  >
                    <Thumb emoji={it.emoji} size={36} />
                    <LrMain>
                      <LrTitle>{it.skuOrReference || (it.id||"").slice(0,8)}</LrTitle>
                      <LrSub>
                        {live
                          ? `Live — but image #${failIdx.join(', #')} failed to upload.`
                          : `Image #${failIdx.join(', #')} failed. Replace before publishing.`}
                      </LrSub>
                    </LrMain>
                    <Chip tone={live ? 'warn' : 'danger'} dot>{live ? 'Live' : 'Draft'}</Chip>
                    <Button
                      kind="accent"
                      size="xs"
                      icon={<Icon name="upload" size={11} color={colors.white} />}
                      onPress={() => navigation.navigate('editor', { id: it.id })}
                    >
                      Replace
                    </Button>
                  </ListRow>
                );
              })}
            </View>
          </Card>
        </View>
      )}

      {/* ── Bottom 2-col: Search misses + Recent activity ── */}
      <View style={{
        flexDirection: isMobile ? 'column' : 'row',
        gap: 16,
        marginTop: 16,
      }}>
        {/* Recent activity */}
        <View style={{ flex: 1 }}>
          <Card
            title="Recent activity"
            sub="Latest changes across the catalog"
            padded={false}
          >
            <View style={{ paddingHorizontal: 16, paddingBottom: 8 }}>
              {recentActivity.length === 0 ? (
                <View style={{ paddingVertical: 10 }}>
                  <LrSub>No recent activity yet.</LrSub>
                </View>
              ) : recentActivity.map((a, i) => (
                <ListRow key={a.id || i} isLast={i === recentActivity.length - 1} onPress={() => navigation.navigate('editor', { id: a.id })}>
                  <Icon name={a.icon as any} size={17} color={a.tone} />
                  <LrMain>
                    <Text style={{
                      fontWeight: fontWeight.regular,
                      fontSize: fontSize.md,
                      color: colors.ink,
                      fontFamily: theme.font.sans,
                    }}>
                      {a.act}{' '}
                      <Text style={{ fontWeight: fontWeight.semibold }}>{a.what}</Text>
                    </Text>
                  </LrMain>
                  <Text style={{
                    fontSize: fontSize.sm,
                    color: colors.ink3,
                    fontFamily: theme.font.sans,
                    ...Platform.select({ web: { whiteSpace: 'nowrap' } as any, default: {} }),
                  }}>
                    {a.when}
                  </Text>
                </ListRow>
              ))}
            </View>
          </Card>
        </View>
      </View>
    </ScrollView>
  );
}
